import { useState } from 'react'

import { Working } from '~/components/working'
import { useWork } from '~/components/use-work'
import { RedoIcon, UndoIcon } from '~/components/icons'
import { Tooltip } from '~/components/ui/tooltip'
import { useToast } from '~/components/toast-context'
import { useApi } from '~/data/data-context'
import { redoDiaryStep, undoDiaryStep, useDiarySteps } from '~/lib/diary-undo'

/**
 * Undoing and doing again — the nutrition spec, Undoing. They stand in the header while the
 * diary is open, in the place a selection's own controls take: what can be done to what is on
 * the page belongs up there, beside the way out of the page, rather than floating over the
 * page it is about.
 *
 * Both are always there, unavailable rather than absent while there is nothing for them to do.
 * A control that comes and goes moves whatever stands beside it every time something is
 * logged, and leaves somebody hunting for a way back that was in a different place a moment
 * ago. Unavailable also says the thing worth knowing: undo is here, there is just nothing
 * behind it yet.
 *
 * The work runs off the data layer's own promise rather than anything this row owns: undoing
 * is a write, and a write outlives the screen that asked for it.
 */
export function DiarySteps() {
  const api = useApi()
  const toast = useToast()
  const { canUndo, canRedo } = useDiarySteps()
  // A step goes to the server before it is done, so the glyph pressed says it is working.
  const work = useWork()
  const [back, setBack] = useState(true)

  const glyph =
    'relative grid size-9 place-items-center rounded-full text-ink-muted transition-colors hover:bg-hover hover:text-ink disabled:pointer-events-none disabled:opacity-35'

  const step = (back: boolean) => {
    if (work.working) return
    setBack(back)
    void work.run(async () => {
      try {
        const said = back ? await undoDiaryStep(api) : await redoDiaryStep(api)
        if (said !== null) {
          toast(back ? `Undid ${said}.` : `${said}, again.`)
        }
      } catch {
        toast('That did not reach us. Try again in a moment.')
      }
    })
  }

  return (
    <>
      <Tooltip label="Undo">
        <button
          type="button"
          aria-label="Undo"
          aria-busy={(work.working && back) || undefined}
          disabled={!canUndo && !(work.working && back)}
          className={glyph}
          onClick={() => {
            step(true)
          }}
        >
          <Working shown={work.shown && back}>
            <UndoIcon aria-hidden className="size-5" />
          </Working>
        </button>
      </Tooltip>
      <Tooltip label="Redo">
        <button
          type="button"
          aria-label="Redo"
          aria-busy={(work.working && !back) || undefined}
          disabled={!canRedo && !(work.working && !back)}
          className={glyph}
          onClick={() => {
            step(false)
          }}
        >
          <Working shown={work.shown && !back}>
            <RedoIcon aria-hidden className="size-5" />
          </Working>
        </button>
      </Tooltip>
    </>
  )
}
